import { SettingsManager } from "@viamap/viamap2-common";
import { message } from "src/components/Message";
import { ExportData } from "src/managers/ExportData";

export const TINGBOG_SCANNED_AKT_PREFIX =
  "https://www.tinglysning.dk/tinglysning/rest/indskannetakt/rd/";
export const TINGBOG_NEW_AKT_PREFIX =
  "https://www.tinglysning.dk/tinglysning/rest/pdf/atd/";

const TINGBOG_DOWNLOAD_ERROR_MESSAGE =
  "Tingbogen might be down currently, try again later";

type TinglysningGatewayResponse = {
  body?: string;
  url?: string;
};

export type TingbogDocumentRef = Partial<{
  dokumentId: string;
  dokumentFilnavn: string;
  aktAliasId: string;
  aktId: string;
}>;

export type TingbogDownloadTarget = { kind: "akt"; value: string };

export function getTingbogDownloadErrorMessage(): string {
  return TINGBOG_DOWNLOAD_ERROR_MESSAGE;
}

export function getTingbogPdfUrl(
  document?: TingbogDocumentRef
): string | undefined {
  if (document?.dokumentFilnavn) {
    return TINGBOG_SCANNED_AKT_PREFIX + document.dokumentFilnavn;
  }
  if (document?.aktAliasId && document?.dokumentId) {
    return TINGBOG_NEW_AKT_PREFIX + document.dokumentId;
  }
  if (!document?.aktId && document?.dokumentId) {
    return TINGBOG_NEW_AKT_PREFIX + document.dokumentId;
  }
  return undefined;
}

export function isTingbogElectronicDocument(
  document?: TingbogDocumentRef
): boolean {
  if (!document) return false;
  if (document.dokumentFilnavn) return false;
  if (document.aktAliasId) return true;
  return Boolean(document.dokumentId && !document.aktId);
}

export function shouldRenderTingbogDownloadButton(
  document?: TingbogDocumentRef
): boolean {
  return Boolean(document?.dokumentFilnavn) || isTingbogElectronicDocument(document);
}

export function getTingbogDownloadTarget(
  document?: TingbogDocumentRef
): TingbogDownloadTarget | undefined {
  if (document?.dokumentFilnavn) {
    return { kind: "akt", value: document.dokumentFilnavn };
  }

  if (isTingbogElectronicDocument(document)) {
    return undefined;
  }

  return undefined;
}

export function getTingbogDownloadFileName(
  label?: string,
  prefix = "tingbog"
): string {
  const normalizedLabel = (label || prefix)
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001F]+/g, "_")
    .replace(/\s+/g, "_");
  const baseName = normalizedLabel.length ? normalizedLabel : prefix;

  return baseName.toLowerCase().endsWith(".pdf")
    ? baseName
    : `${baseName}.pdf`;
}

function extractPdfFromSoapXml(xmlText: string): Blob {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");
  const ns = "http://rep.oio.dk/tinglysning.dk/schema/elektroniskakt/1/";
  const encodedPdf =
    doc.getElementsByTagNameNS(ns, "IndskannetDokumentData")[0]?.textContent?.trim() ||
    doc.getElementsByTagName("IndskannetDokumentData")[0]?.textContent?.trim() ||
    "";

  if (!encodedPdf) {
    throw new Error("PDF data not found in SOAP response");
  }

  const binary = atob(encodedPdf);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: "application/pdf" });
}

async function resolveTingbogAktFileUrl(aktId: string): Promise<string> {
  const pdfServiceUrl = SettingsManager.getSystemSetting("ann.tinglysningApiGatewayHost");
  const pdfServiceApiKey = SettingsManager.getSystemSetting("ann.tinglysningApiGatewayKey");

  if (!pdfServiceUrl || !pdfServiceApiKey) {
    throw new Error("Missing tinglysning API configuration");
  }

  const apiUrl = `${pdfServiceUrl.replace(/\/+$/, "")}/getTinglysningsAkt`;
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": pdfServiceApiKey,
    },
    body: JSON.stringify({
      AktDokumentId: aktId,
      storeToFile: "true",
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    if (response.status === 503 || /TLWebXML.*503|503.*TLWebXML/i.test(errorText)) {
      throw new Error("Tinglysning service is temporarily unavailable. Please try again later.");
    }
    throw new Error(`Tinglysning API error: ${response.status} - ${errorText.substring(0, 200)}`);
  }

  const raw = (await response.json().catch(() => null)) as TinglysningGatewayResponse | null;
  if (!raw) {
    throw new Error("Invalid API response");
  }

  let innerBody: { url?: string } | null = null;
  if (typeof raw.body === "string") {
    try {
      innerBody = JSON.parse(raw.body) as { url?: string };
    } catch {
      innerBody = null;
    }
  }

  const fileUrl = raw.url ?? innerBody?.url;
  if (!fileUrl || typeof fileUrl !== "string") {
    throw new Error("API did not return file URL");
  }

  return fileUrl;
}

export async function fetchTingbogPdfBlob(aktId: string): Promise<Blob> {
  const fileUrl = await resolveTingbogAktFileUrl(aktId);
  const response = await fetch(fileUrl);

  if (!response.ok) {
    throw new Error(`Failed to fetch file from storage (${response.status})`);
  }

  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  const blob = await response.blob();

  if (contentType.includes("xml")) {
    return extractPdfFromSoapXml(await blob.text());
  }

  const blobText = await blob.text();
  if (blobText.trimStart().startsWith("<")) {
    return extractPdfFromSoapXml(blobText);
  }

  return blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
}

export async function downloadTingbogDocument(
  target: TingbogDownloadTarget | undefined,
  fileName?: string
): Promise<void> {
  if (!target) {
    message.error(getTingbogDownloadErrorMessage());
    return;
  }

  try {
    const blob = await fetchTingbogPdfBlob(target.value);
    ExportData.downloadBlob(getTingbogDownloadFileName(fileName), blob);
  } catch (error) {
    console.error("Failed to download Tingbog document", target, error);
    message.error(getTingbogDownloadErrorMessage());
  }
}
